import React, { useState, useCallback } from 'react';
import { View, ScrollView, RefreshControl } from 'react-native';
import {
    HistoryCalendar,
    HistoryHeader,
    HistoryActivitySection,
    GeneralHistoryHeader,
    PatientDocumentsSection,
    HistoryVisitsSection,
} from '../components';
import type { TabType } from '../components';
import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import { useFetch } from 'app/queryProvider';
import { useAuth } from 'app/auth/hooks/useAuth';
import { Loading } from '@shared/components/loading';
import MainErrorWithIcon from '@shared/components/ModularInfoWithIcon';

const History = () => {
    const { meData } = useAuth()

    const [currentDate, setCurrentDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState<TabType>('activities');

    const today = new Date();

    const formattedCurrentDate = format(currentDate, 'yyyy-MM-dd');
    const formattedToday = format(today, 'yyyy-MM-dd');

    const isToday = formattedCurrentDate === formattedToday;

    const weekStartDate = format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    const weekEndDate = format(
        today < addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6)
            ? today
            : addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6),
        'yyyy-MM-dd'
    );

    const { data: historyActivities, isLoading: isLoadingHistoryActivities, isRefetching: isRefetchingHistoryActivities, refetch: refetchHistoryActivities } = useFetch<any>({
        url: `/activities/days-completion-status?start_date=${weekStartDate}&end_date=${weekEndDate}`,
        queryKey: [["pathway", "history-completion-status", weekStartDate]],
        resource: { name: "pathways", id: meData?.current_pathway.id },
        options: {
            enabled: !!meData?.current_pathway.id
        }
    });

    const completedActivityByDay: boolean | undefined = historyActivities?.days[formattedCurrentDate];

    const { data: pathwayActivities, isLoading: isLoadingPathwayActivities, isRefetching: isRefetchingPathwayActivities, refetch: refetchPathwayActivities } = useFetch<any>({
        url: `/activities?date=${formattedCurrentDate}`,
        queryKey: [["pathway", "activities", formattedCurrentDate]],
        resource: { name: "pathways", id: meData?.current_pathway.id },
        options: {
            enabled: !!meData?.current_pathway.id && (isToday || completedActivityByDay !== undefined)
        }
    });


    const weekDaysConfig = {
        monday: { id: 'mon', label: 'L' },
        tuesday: { id: 'tue', label: 'M' },
        wednesday: { id: 'wed', label: 'M' },
        thursday: { id: 'thu', label: 'G' },
        friday: { id: 'fri', label: 'V' },
        saturday: { id: 'sat', label: 'S' },
        sunday: { id: 'sun', label: 'D' },
    } as const;

    const weekDays = Object.values(weekDaysConfig);
    const handleDateSelect = useCallback((date: Date) => {
        setCurrentDate(date);
    }, []);
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const handleDateChange = useCallback((weeks: number) => {
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setDate(newDate.getDate() + weeks * 7);
            if (weeks > 0) {
                const newWeekStart = startOfWeek(newDate, { weekStartsOn: 1 });
                const newWeekDates = Array.from({ length: 7 }, (_, i) => addDays(newWeekStart, i));
                const todayInNewWeek = newWeekDates.some(date => isSameDay(date, today));

                if (todayInNewWeek) {
                    return today;
                }
            }
            return newDate;
        });
    }, []);

    const dates = weekDays.map((_, index) => {
        const date = addDays(weekStart, index);
        return {
            date: format(date, 'd'),
            isToday: isSameDay(date, new Date()),
            isSelected: isSameDay(date, currentDate),
            fullDate: date,
        };
    });
    const currentWeekContainsToday = dates.some(dateInfo => dateInfo.isToday);
    const canNavigateForward = !currentWeekContainsToday;

    const handleTabChange = useCallback((tab: TabType) => {
        setActiveTab(tab);
    }, []);

    const isRefreshing = isRefetchingHistoryActivities || isRefetchingPathwayActivities;
    const handleRefresh = useCallback(() => {
        refetchHistoryActivities();
        refetchPathwayActivities();
    }, [refetchHistoryActivities, refetchPathwayActivities]);

    if (isLoadingHistoryActivities || isLoadingPathwayActivities) {
        return <Loading />
    }

    return (
        <View className="flex-1 bg-white">
            <GeneralHistoryHeader
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />
            <View className="flex-1 px-8">
                {activeTab === 'activities' ? (
                    <>
                        <HistoryHeader
                            currentDate={currentDate}
                            handleDateChange={handleDateChange}
                            canNavigateForward={canNavigateForward}
                        />
                        <HistoryCalendar
                            weekDays={weekDays}
                            dates={dates}
                            handleDateSelect={handleDateSelect}
                            statuses={historyActivities?.days}
                        />
                        <ScrollView
                            className='flex-1 py-4'
                            refreshControl={
                                <RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />
                            }
                        >
                            {(() => {
                                if (!pathwayActivities) {
                                    return (
                                        <MainErrorWithIcon
                                            errorType={
                                                isToday
                                                    ? 'no_activities'
                                                    : 'no_activities_for_other_days'
                                            }
                                        />
                                    );
                                }

                                if (!isToday) {
                                    const hasCompletedActivities = Object.entries(pathwayActivities)
                                        .filter(([key]) => key !== "date")
                                        .some(([_, activities]) =>
                                            Array.isArray(activities) &&
                                            activities.some((activity: any) => activity.status === 'completed')
                                        );

                                    if (!hasCompletedActivities) {
                                        return <MainErrorWithIcon errorType='forgot_activities' />;
                                    }
                                }

                                return <HistoryActivitySection activities={pathwayActivities} isToday={isToday} />;
                            })()}
                        </ScrollView>
                    </>
                ) : activeTab === 'documents' ? (
                    <PatientDocumentsSection />
                ) : (
                    <HistoryVisitsSection />
                )}
            </View>
        </View>
    );
};

export default History;